import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Star, ArrowRight, Building2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Lightbox from "@/components/Lightbox";
import BeforeAfter from "@/components/BeforeAfter";
import GdprConsent from "@/components/GdprConsent";
import { api, imgUrl } from "@/lib/api";

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [gallery, setGallery] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    document.title = "Naši partneri — realizátori zateplenia | Zateplenia.eu";
    api.get("/partners").then((r) => {
      setPartners(r.data);
      // link z review e-mailu: /partneri?ohodnotit={partnerId} → rovno otvorí formulár
      const oid = searchParams.get("ohodnotit");
      if (oid) {
        const p = r.data.find((x) => x.id === oid);
        if (p) { setDetail(p); setReviewOpen(true); }
      }
    }).catch(() => {});
  }, []);

  return (
    <div>
      <Header />
      <section className="pt-36 pb-12 max-w-[1400px] mx-auto px-5 md:px-10">
        <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Overení realizátori</p>
        <h1 className="font-display text-white leading-[0.9] text-5xl sm:text-7xl lg:text-8xl">Naši <span className="text-gold">partneri</span></h1>
        <p className="text-white/60 text-lg max-w-2xl mt-6">Preverení realizátori zateplenia po celom Slovensku. V každom okrese máme overených partnerov.</p>
        <div className="mt-8 flex gap-4">
          <Link to="/stat-sa-partnerom" className="bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center gap-2">Staňte sa partnerom <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/partner" data-testid="partner-login-link" className="border border-white/20 text-white px-6 py-3 font-bold uppercase tracking-wider hover:border-gold">Partner portál</Link>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
        {partners.length === 0 && <p className="text-white/40 border border-white/10 p-10 text-center">Zoznam partnerov pripravujeme.</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="partners-directory">
          {partners.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              data-testid={`partner-card-${p.id}`} className="border border-white/10 bg-ink-2 p-6 hover:border-gold/40 transition-colors">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-ink border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                  {p.logo ? <img src={imgUrl(p.logo)} alt={p.company || p.name} className="w-full h-full object-contain" /> : <Building2 className="w-7 h-7 text-gold" />}
                </div>
                <div>
                  <button onClick={() => setDetail(p)} data-testid={`partner-name-${p.id}`} className="font-display text-2xl text-white leading-none text-left hover:text-gold transition-colors cursor-pointer">{p.company || p.name}</button>
                  {p.rating ? <div className="flex items-center gap-1 text-gold text-sm mt-1"><Star className="w-4 h-4 fill-gold" /> {p.rating} <span className="text-white/40">({p.reviews_count || 0})</span></div> : null}
                </div>
              </div>
              {p.bio && <p className="text-white/60 text-sm leading-relaxed mb-4">{p.bio}</p>}
              {(p.districts || []).length > 0 && (
                <div className="flex items-center gap-2 text-white/50 text-xs mb-3">
                  <MapPin className="w-4 h-4 text-gold" /> {(p.districts || []).slice(0, 4).join(", ")}{(p.districts || []).length > 4 ? "…" : ""}
                </div>
              )}
              {(() => {
                const imgs = (p.references || []).flatMap((r) => (r.images && r.images.length ? r.images : (r.image ? [{ url: r.image, label: "" }] : [])))
                  .map((im) => (typeof im === "string" ? { url: im, label: "" } : im));
                if (!imgs.length) return null;
                return (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {imgs.slice(0, 3).map((im, idx) => (
                      <button key={idx} onClick={() => setGallery({ images: imgs, index: idx })} className="relative aspect-square overflow-hidden border border-white/10 group">
                        <img src={imgUrl(im.url)} alt={p.company || p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        {im.label && <span className="absolute top-0 left-0 bg-gold text-ink text-[9px] font-bold px-1 uppercase">{im.label}</span>}
                        {idx === 2 && imgs.length > 3 && <span className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-mono2 text-sm">+{imgs.length - 3}</span>}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </motion.div>
          ))}
        </div>
      </section>
      <Footer />
      {gallery && <Lightbox images={gallery.images} index={gallery.index} onClose={() => setGallery(null)} />}
      {detail && <PartnerCardModal partner={detail} onClose={() => { setDetail(null); setReviewOpen(false); }} onGallery={setGallery} reviewOpen={reviewOpen} />}
    </div>
  );
}

function PartnerCardModal({ partner: p, onClose, onGallery, reviewOpen = false }) {
  const imgs = (p.references || []).flatMap((r) => (r.images && r.images.length ? r.images : (r.image ? [{ url: r.image, label: "" }] : [])))
    .map((im) => (typeof im === "string" ? { url: im, label: "" } : im));
  return (
    <div className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="partner-vizitka">
      <div className="bg-ink-2 border border-gold/30 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative p-8">
          <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="partner-vizitka-close"><X className="w-6 h-6" /></button>
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 bg-ink border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
              {p.logo ? <img src={imgUrl(p.logo)} alt={p.company || p.name} className="w-full h-full object-contain" /> : <Building2 className="w-9 h-9 text-gold" />}
            </div>
            <div>
              <p className="font-mono2 text-gold uppercase tracking-[0.25em] text-[10px] mb-1">Overený partner</p>
              <h3 className="font-display text-3xl text-white leading-none">{p.company || p.name}</h3>
              {p.rating ? <div className="flex items-center gap-1 text-gold text-sm mt-2"><Star className="w-4 h-4 fill-gold" /> {p.rating} <span className="text-white/40">({p.reviews_count || 0} hodnotení)</span></div> : null}
            </div>
          </div>

          {/* Districts under the name */}
          <div className="mt-6">
            <p className="font-mono2 text-white/40 uppercase tracking-widest text-[10px] mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-gold" /> Pôsobí v okresoch</p>
            <div className="flex flex-wrap gap-2" data-testid="partner-vizitka-districts">
              {(p.districts || []).length ? (p.districts || []).map((d) => (
                <span key={d} className="px-3 py-1 border border-gold/30 bg-gold/5 text-gold text-xs">{d}</span>
              )) : <span className="text-white/40 text-sm">Okresy pripravujeme.</span>}
            </div>
          </div>

          {p.bio && <p className="text-white/70 text-sm leading-relaxed mt-6 border-t border-white/10 pt-6">{p.bio}</p>}

          {imgs.length > 0 && (() => {
            const pred = imgs.find((im) => im.label === "pred");
            const po = imgs.find((im) => im.label !== "pred");
            const gridImgs = pred && po ? imgs.filter((im) => im !== pred && im !== po) : imgs;
            return (
            <div className="mt-6">
              <p className="font-mono2 text-white/40 uppercase tracking-widest text-[10px] mb-3">Realizácie</p>
              {pred && po && (
                <div className="mb-4">
                  <BeforeAfter before={pred.url} after={po.url} alt={p.company || p.name} testid="ba-slider" height="h-56 md:h-80" />
                  <p className="text-white/40 text-[11px] mt-1.5 text-center">Potiahnite zlatý deliť — porovnanie pred / po</p>
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {gridImgs.map((im, idx) => (
                  <button key={idx} onClick={() => { onClose(); onGallery({ images: gridImgs, index: idx }); }} className="relative aspect-square overflow-hidden border border-white/10 group">
                    <img src={imgUrl(im.url)} alt={p.company || p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {im.label && <span className="absolute top-0 left-0 bg-gold text-ink text-[9px] font-bold px-1 uppercase">{im.label}</span>}
                  </button>
                ))}
              </div>
            </div>
            );
          })()}

          <PartnerReviews partnerId={p.id} initialOpen={reviewOpen} />

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/#kalkulacka" onClick={onClose} className="flex-1 bg-gold text-ink py-3 font-bold uppercase tracking-wider text-sm text-center hover:bg-gold-light transition-colors" data-testid="partner-vizitka-cta">
              Získať cenovú ponuku
            </Link>
            {p.google_url && (
              <a href={p.google_url} target="_blank" rel="noreferrer" className="flex-1 border border-white/20 text-white py-3 font-bold uppercase tracking-wider text-sm text-center hover:border-gold transition-colors">
                Recenzie Google
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PartnerReviews({ partnerId, initialOpen = false }) {
  const [data, setData] = useState(null);
  const [formOpen, setFormOpen] = useState(initialOpen);
  const [form, setForm] = useState({ name: "", town: "", text: "" });
  const [rating, setRating] = useState(5);
  const [gdpr, setGdpr] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (partnerId) api.get(`/partners/${partnerId}/reviews`).then((r) => setData(r.data)).catch(() => setData({ reviews: [], count: 0, avg: null }));
  }, [partnerId]);

  const submit = async () => {
    if (!form.name.trim() || !form.text.trim()) { toast.error("Vyplňte meno a text hodnotenia."); return; }
    if (!gdpr) { toast.error("Potvrďte súhlas so spracovaním údajov."); return; }
    setSending(true);
    try {
      await api.post(`/partners/${partnerId}/reviews`, { ...form, rating });
      setSent(true);
    } catch { toast.error("Hodnotenie sa nepodarilo odoslať."); }
    finally { setSending(false); }
  };

  if (!data) return null;
  return (
    <div className="mt-6 border-t border-white/10 pt-6" data-testid="partner-reviews-public">
      <div className="flex items-center justify-between mb-3">
        <p className="font-mono2 text-white/40 uppercase tracking-widest text-[10px] flex items-center gap-2">
          <Star className="w-4 h-4 text-gold" /> Hodnotenia zákazníkov{data.count ? ` (${data.count})` : ""}
        </p>
        {!formOpen && !sent && (
          <button onClick={() => setFormOpen(true)} data-testid="review-open-form" className="text-gold text-xs hover:underline">+ Ohodnotiť firmu</button>
        )}
      </div>
      {data.reviews.length === 0 && !formOpen && !sent && (
        <p className="text-white/35 text-sm">Zatiaľ žiadne hodnotenia — buďte prvý.</p>
      )}
      <div className="space-y-3">
        {data.reviews.map((r, i) => (
          <div key={i} className="border border-white/10 p-3.5" data-testid={`partner-review-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-white text-sm font-medium">{r.name}{r.town ? ` · ${r.town}` : ""}</span>
              <span className="text-gold text-xs font-mono2">{"★".repeat(r.rating)}<span className="text-white/20">{"★".repeat(5 - r.rating)}</span></span>
            </div>
            <p className="text-white/60 text-sm mt-1 leading-relaxed">{r.text}</p>
          </div>
        ))}
      </div>

      {sent && (
        <div className="mt-4 border border-gold/30 bg-gold/5 p-4 text-center" data-testid="review-thanks-partner">
          <p className="text-gold font-semibold text-sm">Ďakujeme za hodnotenie!</p>
          <p className="text-white/50 text-xs mt-1">Zobrazí sa po schválení administrátorom.</p>
        </div>
      )}

      {formOpen && !sent && (
        <div className="mt-4 border border-gold/25 p-4 space-y-3" data-testid="partner-review-form">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating(n)} data-testid={`partner-review-star-${n}`} aria-label={`${n} hviezdičiek`}>
                <Star className={`w-7 h-7 ${n <= rating ? "fill-gold text-gold" : "text-white/25"}`} />
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Vaše meno" data-testid="partner-review-name"
              className="bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-2 text-sm" />
            <input value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} placeholder="Mesto (voliteľné)" data-testid="partner-review-town"
              className="bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-2 text-sm" />
          </div>
          <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={4}
            placeholder="Ako ste boli spokojní s realizáciou, komunikáciou a výsledkom?" data-testid="partner-review-text"
            className="w-full bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-2 text-sm" />
          <GdprConsent checked={gdpr} onChange={setGdpr} testid="partner-review-gdpr" />
          <button onClick={submit} disabled={sending} data-testid="partner-review-submit"
            className="bg-gold text-ink px-6 py-2.5 font-bold uppercase tracking-wider text-xs hover:bg-gold-light disabled:opacity-50 flex items-center gap-2">
            {sending && <Loader2 className="w-4 h-4 animate-spin" />} Odoslať hodnotenie
          </button>
        </div>
      )}
    </div>
  );
}
